import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import keishIcon from "@/assets/icon-green.svg";
import keishIconWhite from "@/assets/icon-white.svg";
import ContactSheet from "@/components/ContactSheet";
import { useTheme } from "@/components/ThemeProvider";
import SEO from "@/components/SEO";
import MoreWorkCarousel from "@/components/MoreWorkCarousel";
import FooterSection from "@/components/FooterSection";
import coterie1 from "@/assets/shop/coterie-1.jpeg";
import coterie2 from "@/assets/shop/coterie-2.jpg";
import coterie3 from "@/assets/shop/coterie-3.jpg";
import coterie4 from "@/assets/shop/coterie-4.jpg";
import coterie5 from "@/assets/shop/coterie-5.jpg";

const images = [coterie1, coterie2, coterie3, coterie4, coterie5];

const CoterieCaseStudy = () => {
  const { mode } = useTheme();
  const isDark = mode === "dark";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Coterie — Real Estate Template"
        description="A modern Framer template for real estate professionals with full CMS control over listings, community pages, and room styles."
        canonical="/work/coterie"
      />

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 lg:px-14 py-4 backdrop-blur-xl border-b transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsla(0, 0%, 9%, 0.9)" : "hsla(45, 100%, 98%, 0.9)",
          color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
          borderColor: isDark ? "hsla(27, 61%, 92%, 0.1)" : "hsl(45, 10%, 88%)",
        }}
      >
        <Link to="/" className="flex items-center gap-2 group">
          <ArrowLeft size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
          <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6" />
          <span className="font-body text-ts-2xs font-semibold tracking-[0.2em] uppercase">KEISH</span>
        </Link>
        <div className="flex items-center gap-4">
          <ContactSheet>
            <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-50 hover:opacity-100 transition-opacity">
              Work With Us
            </button>
          </ContactSheet>
        </div>
      </header>

      {/* Hero — matches CaseStudy layout */}
      <div className="pt-28 md:pt-36">
        <div className="max-w-[700px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="pb-8 md:pb-10 mb-8 md:mb-10 border-b border-border"
          >
            <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-4">
              Framer Template Development — Real Estate
            </p>
            <h1 className="font-display text-5xl md:text-7xl italic text-foreground mb-3">
              Coterie
            </h1>
            <p className="font-body text-ts-sm text-muted-foreground tracking-wide max-w-[560px] leading-relaxed mb-5">
              A sleek, customizable template designed for real estate professionals who want a polished, standout online presence — without the cookie-cutter look.
            </p>

            <div className="flex items-center gap-4">
              <a
                href="https://contra.com/payment-link/D8Ql5hHf-coterie-real-estate-industry-template"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                Buy for $65
                <ArrowUpRight size={12} />
              </a>
              <a
                href="https://coterie.framer.website/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                Live Preview
                <ExternalLink size={10} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content — 700px max-width, consistent spacing */}
      <div className="max-w-[700px] mx-auto px-5 md:px-10 pb-20 md:pb-28 space-y-14 md:space-y-20">
        {/* About */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
            Built for agents who want control
          </h2>
          <div className="font-body text-ts-xs text-foreground/80 leading-[1.9] space-y-4">
            <p>
              Coterie is a modern, refined template built for real estate professionals who want a website that feels as polished as the properties they represent. With a clean design and flexible customization options, it helps you create a site that reflects your expertise without feeling generic.
            </p>
            <p>
              No fluff, just a well-crafted foundation to showcase your listings and brand with confidence. Every page, every listing, every detail — managed through Framer's built-in CMS.
            </p>
          </div>
        </motion.section>

        {/* CMS emphasis */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
            Complete CMS control
          </h2>
          <p className="font-body text-ts-xs text-foreground/80 leading-[1.9] mb-6">
            Every piece of content is CMS-driven. Your team adds listings, updates community info, and manages room styles directly from Framer — no developer handoff, no code edits, no waiting.
          </p>
          <ul className="space-y-3">
            {[
              "Property listings managed entirely through CMS collections",
              "Community pages with dynamic content blocks",
              "Room styles gallery with filterable categories",
              "Contact forms and CTAs pre-wired and ready",
              "SEO metadata auto-generated per listing",
            ].map((item) => (
              <li
                key={item}
                className="font-body text-ts-2xs text-muted-foreground flex items-start gap-3"
              >
                <span className="w-1 h-1 rounded-full bg-foreground/40 flex-shrink-0 mt-[7px]" />
                {item}
              </li>
            ))}
          </ul>
        </motion.section>

        {/* Features grid */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-6">
            What's included
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-0">
            {[
              "A11y Optimized",
              "Animations & Effects",
              "Automated SEO",
              "Built-in Analytics",
              "CMS Collections",
              "Reusable Components",
              "Custom Cursors",
              "Contact Forms",
              "Layout Templates",
              "Project Styles",
              "Visual Breakpoints",
              "5 Pages Included",
            ].map((feature) => (
              <div
                key={feature}
                className="font-body text-ts-2xs text-foreground/70 flex items-center gap-2 py-2.5 border-b border-border/40"
              >
                <span className="w-1 h-1 rounded-full bg-foreground/40 flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </motion.section>

        {/* Pages */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
            Pages
          </h2>
          <p className="font-body text-ts-xs text-muted-foreground leading-[1.9] mb-4">
            Five thoughtfully designed pages, each CMS-ready and fully responsive:
          </p>
          <div className="space-y-0">
            {["Home", "About", "Community", "Contact"].map((page) => (
              <div
                key={page}
                className="font-body text-ts-xs text-foreground/80 py-3 border-b border-border/30"
              >
                {page}
              </div>
            ))}

            {/* Room Styles — CMS-controlled */}
            <div className="py-3 border-b border-border/30">
              <div className="flex items-center justify-between">
                <span className="font-body text-ts-xs text-foreground/80">Room Styles</span>
                <a
                  href="https://www.framer.com/academy/lessons/cms-pages-dynamic-content"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary font-body text-[10px] font-medium tracking-[0.1em] uppercase hover:bg-primary/20 transition-colors"
                >
                  CMS-controlled
                  <ExternalLink size={8} />
                </a>
              </div>
              <div className="mt-2.5 ml-4 space-y-1.5">
                {["Style Details", "Available Units"].map((sub) => (
                  <div
                    key={sub}
                    className="font-body text-[11px] text-muted-foreground flex items-center gap-2"
                  >
                    <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                    {sub}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Image gallery — full bleed within the 700px container */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-4">
            {images.map((img, i) => (
              <motion.img
                key={i}
                src={img}
                alt={`Coterie template screenshot ${i + 1}`}
                className="w-full"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                loading="lazy"
              />
            ))}
          </div>
        </motion.section>
      </div>

      {/* Relevant Tags */}
      <div className="max-w-[700px] mx-auto px-5 md:px-10 pb-14">
        <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-4">
          Relevant Tags
        </p>
        <div className="flex flex-wrap gap-2">
          {["Framer", "Real Estate", "Template", "CMS", "No-Code", "Responsive"].map((tag) => (
            <span
              key={tag}
              className="px-3 py-1.5 border border-border font-body text-ts-2xs text-muted-foreground tracking-wide"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Compact CTA bar — matches other case studies */}
      <div className="border-t border-border">
        <div className="max-w-[700px] mx-auto px-5 md:px-10 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="font-display text-xl md:text-2xl italic text-foreground/90">
            Interested in working together?
          </p>
          <ContactSheet>
            <button className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground/20 text-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase hover:bg-foreground hover:text-background transition-colors">
              Work With Us
              <ArrowUpRight size={10} />
            </button>
          </ContactSheet>
        </div>
      </div>

      {/* More Work */}
      <MoreWorkCarousel currentSlug="coterie" />

      <FooterSection />
    </div>
  );
};

export default CoterieCaseStudy;